import { Add as AddIcon } from '@mui/icons-material';
import {
  Badge,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
} from '@mui/material';
import { InputProps } from '@mui/material/Input';
import { red } from '@mui/material/colors';
import * as Schema from 'generated/graphql/schema';
import { TFunction } from 'i18next';
import { startCase } from 'lodash';
import * as React from 'react';
import { FC } from 'react';
import { ValidatorForm } from 'react-form-validator-core';
import { useTranslation } from 'react-i18next';

import { DiscreteStatesValidator } from '@/components/validators-forms/discrete-states-validator';
import InputValidator, { SelectValidator } from '@/components/validators-forms/input-validator';
import { convertMultiplierOffset, convertSensorMinMax, parseAnalogRange } from '@/lib/config-helpers';
import * as Types from '@/types';
import { SyncBadge } from '@/views/administration/device/config/device-config-card';
import WrapRequiredFieldsDescriptionComponent from '@/views/common-components/wrap-required-fileds-description';
import {
  SensorSettingsClasses,
  SensorSettingsState,
  UpdateConfigInput,
} from '@/views/dialogs/sensor-settings/sensor-settings-dialog';

import { TabPanelComponent, TabPropsType } from '.';

const isShown = (state: SensorSettingsState): boolean => {
  return (
    state.peripheralType === 'SENSOR' &&
    state.config?.sensor?.type !== Schema.SensorType.EVENT &&
    state.config?.sensor?.type !== Schema.SensorType.MANUAL_PROCESS
  );
};

const hasOnState = (state: SensorSettingsState): boolean => {
  return (state?.config?.sensor?.discreteConfig?.onStates?.length ?? 0) > 0;
};

const hasOffState = (state: SensorSettingsState): boolean => {
  return (state?.config?.sensor?.discreteConfig?.offStates?.length ?? 0) > 0;
};

const hasDiscreteStates = (state: SensorSettingsState): boolean => {
  return hasOnState(state) && hasOffState(state);
};

const isValid = (state: SensorSettingsState): boolean => {
  const { type, analogInputRange, dataMultiplier, analogOffset } = state.config?.sensor ?? {};
  switch (type) {
    case Schema.SensorType.MEASUREMENT:
      const useAnalogInputRange = !!analogInputRange;
      const useDataMultiplier =
        !analogInputRange && typeof dataMultiplier === 'number' && typeof analogOffset === 'number';
      return (!useAnalogInputRange && useDataMultiplier) || (useAnalogInputRange && !useDataMultiplier);
    // TS does not have XOR :(
    case Schema.SensorType.DISCRETE:
      return hasDiscreteStates(state) && typeof dataMultiplier === 'number' && dataMultiplier > 0;
    default:
      return typeof dataMultiplier === 'number' && dataMultiplier > 0;
  }
};

const formatAnalogRange = (t: TFunction, type: string) => startCase(type.toLowerCase());

export const TabProps: TabPropsType = {
  isShown,
  isValid,
  label: (t) => t(['device:sensorSetup'], { defaultValue: 'Sensor' }),
};

ValidatorForm.addValidationRule('notZero', (value) => {
  return value !== 0;
});

ValidatorForm.addValidationRule('notEmpty', (value) => {
  if (value instanceof Array) {
    return value.length > 0;
  }
  return false;
});

export const TabPanel: TabPanelComponent<{
  sensor: Partial<Schema.Sensor>;
  enumTypes: Types.EnumTypes;
}> = ({ enumTypes, ...props }) => {
  if (!isShown(props.state)) {
    return null;
  }

  switch (props.state.config?.sensor?.type) {
    case Schema.SensorType.MEASUREMENT:
      return <MeasurementContent enumTypes={enumTypes} {...props}></MeasurementContent>;
    case Schema.SensorType.COUNTER_ACCUMULATE:
      return <CounterAccContent {...props} />;
    case Schema.SensorType.DISCRETE:
      return <DiscreteContent {...props} />;
    default:
      return <CounterContent {...props} />;
  }
};

type HtmlEventHandler = (event: React.KeyboardEvent<HTMLDivElement>) => void;

const getPressEnterHandler = (elementId: string, onPressEnter: (stateValue: number) => void): HtmlEventHandler => {
  const handleAdd = getAddHandler(elementId, onPressEnter);

  return (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!event || !event.key || event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    handleAdd();
  };
};

const getAddHandler = (elementId: string, onAdd: (stateValue: number) => void) => {
  return () => {
    const input = document.getElementById(elementId) as HTMLInputElement;
    const stateValue = parseInt(input.value, 10);
    if (!Number.isNaN(stateValue)) {
      onAdd(stateValue);
    }
    input.value = '';
  };
};

const wiringOptions = Object.keys(Schema.SensorWiring).sort();

const WiringSelection: FC<{
  wiring: Omit<Schema.SensorWiringState, '__typename'> | null;
  classes: SensorSettingsClasses;
  handleChange: (newConfig: Partial<UpdateConfigInput>, meta?: { name?: string; description?: string }) => void;
}> = ({ wiring, classes, handleChange }) => {
  const [t] = useTranslation();
  if (!wiring) {
    return null;
  }

  const reportedWiring = wiring.reported || null;
  const desiredWiring = wiring.desired;
  const onWiringChange: InputProps['onChange'] = ({ target: { value } }) => {
    handleChange({
      sensor: {
        wiring: {
          reported: reportedWiring,
          desired: value as Schema.SensorWiring,
        },
      },
    });
  };

  return (
    <FormControl variant="filled" fullWidth className={classes.formControl}>
      <Badge badgeContent={<SyncBadge pendingChanges={reportedWiring !== desiredWiring} />}>
        <SelectValidator
          className={classes.selectFields}
          containerClass={classes.containerClass}
          name="type"
          label={t(['device:sensorWiring'], { defaultValue: 'The type of wiring for the attached sensor' })}
          value={desiredWiring}
          onChange={onWiringChange}
        >
          {wiringOptions.map((wiringType) => {
            return (
              <MenuItem key={wiringType} value={wiringType}>
                {wiringType}
              </MenuItem>
            );
          })}
        </SelectValidator>
      </Badge>
    </FormControl>
  );
};

const DiscreteContent: TabPanelComponent = ({ handleChange, state, invalidFormMessage, classes, t }) => {
  const onDataMultiplierChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    handleChange({ sensor: { dataMultiplier: val.trim().length ? parseFloat(val) : undefined } });
  };

  const discreteConfig = state.config?.sensor?.discreteConfig || ({} as Schema.DiscreteConfigInput);

  const handleAddOnState = (stateValue: number) => {
    const currentOnStates = discreteConfig.onStates ?? [];
    const currentOffStates = discreteConfig.offStates ?? [];

    if (!currentOnStates.includes(stateValue)) {
      const index = currentOffStates.indexOf(stateValue);
      if (index >= 0) {
        handleChange({
          sensor: {
            discreteConfig: {
              ...discreteConfig,
              onStates: [...currentOnStates, stateValue],
              offStates: currentOffStates.filter((_, i) => index !== i), // remove matching state
            },
          },
        });
      } else {
        handleChange({
          sensor: {
            discreteConfig: {
              ...discreteConfig,
              onStates: [...currentOnStates, stateValue],
            },
          },
        });
      }
    }
  };

  const handleAddOffState = (stateValue: number) => {
    const currentOnStates = state?.config?.sensor?.discreteConfig?.onStates ?? [];
    const currentOffStates = state?.config?.sensor?.discreteConfig?.offStates ?? [];
    if (!currentOffStates.includes(stateValue)) {
      const index = currentOnStates.indexOf(stateValue);
      if (index >= 0) {
        handleChange({
          sensor: {
            discreteConfig: {
              ...discreteConfig,
              onStates: currentOnStates.filter((_, i) => index !== i), // remove matching state
              offStates: [...currentOffStates, stateValue],
            },
          },
        });
      } else {
        handleChange({
          sensor: {
            discreteConfig: {
              ...discreteConfig,
              offStates: [...currentOffStates, stateValue],
            },
          },
        });
      }
    }
  };

  const handleDeleteState = (stateValue: number) => {
    const currentOnStates = state?.config?.sensor?.discreteConfig?.onStates ?? [];
    const currentOffStates = state?.config?.sensor?.discreteConfig?.offStates ?? [];

    const onStateIndex = currentOnStates.indexOf(stateValue);
    const offStateIndex = currentOffStates.indexOf(stateValue);

    if (onStateIndex >= 0) {
      handleChange({
        sensor: {
          discreteConfig: {
            ...discreteConfig,
            onStates: currentOnStates.filter((_, i) => onStateIndex !== i), // remove matching state,
          },
        },
      });
    } else if (offStateIndex >= 0) {
      handleChange({
        sensor: {
          discreteConfig: {
            ...discreteConfig,
            offStates: currentOffStates.filter((_, i) => offStateIndex !== i), // remove matching state,
          },
        },
      });
    }
  };

  const onDefaultStateChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    handleChange({
      sensor: {
        discreteConfig: {
          ...discreteConfig,
          defaultState: e.target.value as Schema.DiscreteDeviceState,
        },
      },
    });
  };

  return (
    <div className={classes.tabPanel}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <InputValidator
            className={classes.textFields}
            containerClass={classes.containerClass}
            name="dataMultiplier"
            type="number"
            label={t(['device:dataMultiplier'], { defaultValue: 'Data multiplier' })}
            validators={['required', 'isPositive', 'notZero']}
            errorMessages={[
              t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' }),
              t(['shared:mustBePositive'], { defaultValue: 'must be a positive number' }),
              t(['shared:mustBePositive'], { defaultValue: 'must be a positive number' }),
            ]}
            value={state?.config?.sensor?.dataMultiplier}
            helperText={
              !!state?.config?.sensor?.dataMultiplier && state?.config?.sensor?.dataMultiplier > 0 ? (
                <span>
                  {t(['device:configurationDataMultiplierExplanation'], {
                    defaultValue:
                      'Future incoming data from the sensor will be multiplied by {{dataMultiplier}}. Old data will not be affected. If you change this value, remember to change target line speeds for your batches / products as well',
                    dataMultiplier: state?.config?.sensor?.dataMultiplier ?? 1,
                  })}
                </span>
              ) : undefined
            }
            onChange={onDataMultiplierChange}
          />
        </Grid>

        <Grid item xs={12}>
          <div className={classes.discreteContainer}>
            <TextField
              variant="filled"
              id="onStatesInput"
              name="onStates"
              type="number"
              label={t(['device:onStates'], { defaultValue: 'ON States' })}
              style={{ flex: '1 1 auto' }}
              error={!hasOnState(state)}
              helperText={
                <span>
                  {t(['device:discreteStatesOnExplanation'], {
                    defaultValue:
                      'Specify the sensor states that correspond to the line being on (remember to click the + to add the state)',
                  })}
                </span>
              }
              onKeyPress={getPressEnterHandler('onStatesInput', handleAddOnState)}
            />

            <Button className={classes.discreteAddButton} onClick={getAddHandler('onStatesInput', handleAddOnState)}>
              <AddIcon />
            </Button>
          </div>

          <DiscreteStatesValidator
            validators={['notEmpty']}
            t={t}
            value={state?.config?.sensor?.discreteConfig?.onStates ?? []}
            onClickDelete={handleDeleteState}
          ></DiscreteStatesValidator>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.discreteContainer}>
            <TextField
              variant="filled"
              id="offStatesInput"
              name="offStates"
              type="number"
              label={t(['device:offStates'], { defaultValue: 'OFF States' })}
              style={{ flex: '1 1 auto' }}
              error={!hasOffState(state)}
              helperText={
                <span>
                  {t(['device:discreteStatesOffExplanation'], {
                    defaultValue:
                      'Specify the sensor states that correspond to the line being off (remember to click the + to add the state)',
                  })}
                </span>
              }
              onKeyPress={getPressEnterHandler('offStatesInput', handleAddOffState)}
            />

            <Button className={classes.discreteAddButton} onClick={getAddHandler('offStatesInput', handleAddOffState)}>
              <AddIcon />
            </Button>
          </div>
          <DiscreteStatesValidator
            validators={['notEmpty']}
            t={t}
            value={state?.config?.sensor?.discreteConfig?.offStates ?? []}
            onClickDelete={handleDeleteState}
          ></DiscreteStatesValidator>
        </Grid>

        <Grid item xs={12}>
          <SelectValidator
            className={classes.selectFields}
            containerClass={classes.containerClass}
            name="type"
            label={`${t(['device:defaultState'], { defaultValue: 'Default state' })} *`}
            helperText={
              <span>
                {t(['device:discreteStatesDefaultExplanation'], {
                  defaultValue:
                    'If the value is not caught by either of the above states, it will fallback to the default state',
                })}
              </span>
            }
            value={state?.config?.sensor?.discreteConfig?.defaultState ?? 'OFF'}
            validators={['required']}
            errorMessages={[t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' })]}
            onChange={onDefaultStateChange}
          >
            <MenuItem key="off" value={'OFF'}>
              {t(['shared:off'], { defaultValue: 'Off' })}
            </MenuItem>
            <MenuItem key="on" value={'ON'}>
              {t(['shared:on'], { defaultValue: 'On' })}
            </MenuItem>
          </SelectValidator>
        </Grid>
      </Grid>
      {invalidFormMessage}
      <br />
      <WrapRequiredFieldsDescriptionComponent />
    </div>
  );
};

const CounterContent: TabPanelComponent = ({ handleChange, state, invalidFormMessage, classes, t }) => {
  ValidatorForm.addValidationRule('notZero', (value) => {
    return value !== 0;
  });
  const onDataMultiplierChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    handleChange({ sensor: { dataMultiplier: val.trim().length ? parseFloat(val) : undefined } });
  };

  return (
    <div className={classes.tabPanel}>
      <InputValidator
        className={classes.textFields}
        containerClass={classes.containerClass}
        name="dataMultiplier"
        type="number"
        label={t(['device:dataMultiplier'], { defaultValue: 'Data multiplier' })}
        validators={['required', 'isPositive', 'notZero']}
        errorMessages={[
          t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' }),
          t(['shared:mustBePositive'], { defaultValue: 'must be a positive number' }),
          t(['shared:mustBePositive'], { defaultValue: 'must be a positive number' }),
        ]}
        value={state?.config?.sensor?.dataMultiplier}
        helperText={
          !!state?.config?.sensor?.dataMultiplier && state?.config?.sensor?.dataMultiplier > 0 ? (
            <span>
              {t(['device:configurationDataMultiplierExplanation'], {
                defaultValue:
                  'Future incoming data from the sensor will be multiplied by {{dataMultiplier}}. Old data will not be affected. If you change this value, remember to change target line speeds for your batches / products as well',
                dataMultiplier: state?.config?.sensor?.dataMultiplier ?? 1,
              })}
            </span>
          ) : undefined
        }
        onChange={onDataMultiplierChange}
      />
      {invalidFormMessage}
      <WiringSelection handleChange={handleChange} classes={classes} wiring={state.config?.sensor?.wiring || null} />
      <WrapRequiredFieldsDescriptionComponent />
      {/* Only counters get energy meter */}
      {state.config?.sensor?.type === Schema.SensorType.COUNTER ? (
        <>
          <div className={classes.containerClass}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={state.config?.sensor?.energyMeter ?? false}
                  onChange={(e) => {
                    if (e.target.checked && state.config?.sensor?.findStops) {
                      // Then we also disable findStops
                      handleChange({ sensor: { energyMeter: true, findStops: false } });
                    } else {
                      handleChange({ sensor: { energyMeter: e.target.checked } });
                    }
                  }}
                />
              }
              label={t('device:energyMeter', { defaultValue: 'Energy Meter' })}
              className={classes.textFields}
            />
          </div>
          <WrapRequiredFieldsDescriptionComponent
            overrideTitle={t('device:energyMeterDescription', {
              defaultValue: 'Enable if the sensor is an energy meter.',
            })}
          />
        </>
      ) : null}
    </div>
  );
};

ValidatorForm.addValidationRule('notZero', (value) => {
  return value !== 0;
});

const CounterAccContent: TabPanelComponent = ({ handleChange, state, invalidFormMessage, classes, t }) => {
  const accumulatorConfig = state.config?.sensor?.accumulatorConfig || ({} as Schema.AccumulatorConfigInput);
  const onDataMultiplierChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    handleChange({ sensor: { dataMultiplier: val.trim().length ? parseFloat(val) : undefined } });
  };

  const onRolloverValueChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    handleChange({
      sensor: {
        accumulatorConfig: {
          ...accumulatorConfig,
          rolloverValue: val.trim().length ? parseFloat(val) : undefined,
        },
      },
    });
  };

  const onPowerCheck = (e: React.ChangeEvent<HTMLInputElement>) =>
    handleChange({
      sensor: {
        accumulatorConfig: { ...accumulatorConfig, resetsOnPowerOff: e.target.checked },
      },
    });

  const onRolloverThresholdChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    handleChange({
      sensor: {
        accumulatorConfig: {
          ...accumulatorConfig,
          rolloverThreshold: val.trim().length ? parseFloat(val) : undefined,
        },
      },
    });
  };

  return (
    <div className={classes.tabPanel}>
      <InputValidator
        className={classes.textFields}
        containerClass={classes.containerClass}
        name="dataMultiplier"
        type="number"
        label={t(['device:dataMultiplier'], { defaultValue: 'Data multiplier' })}
        validators={['required', 'isPositive', 'notZero']}
        errorMessages={[
          t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' }),
          t(['shared:mustBePositive'], { defaultValue: 'must be a positive number' }),
          t(['shared:mustBePositive'], { defaultValue: 'must be a positive number' }),
        ]}
        value={state?.config?.sensor?.dataMultiplier}
        helperText={
          !!state?.config?.sensor?.dataMultiplier && state?.config?.sensor?.dataMultiplier > 0 ? (
            <span>
              {t(['device:configurationDataMultiplierExplanation'], {
                defaultValue:
                  'Future incoming data from the sensor will be multiplied by {{dataMultiplier}}. Old data will not be affected. If you change this value, remember to change target line speeds for your batches / products as well',
                dataMultiplier: state?.config?.sensor?.dataMultiplier ?? 1,
              })}
            </span>
          ) : undefined
        }
        onChange={onDataMultiplierChange}
      />
      <WiringSelection handleChange={handleChange} classes={classes} wiring={state.config?.sensor?.wiring || null} />
      <InputValidator
        className={classes.textFields}
        containerClass={classes.containerClass}
        name="rolloverValue"
        type="number"
        label={t(['device:rolloverValue'], { defaultValue: 'Rollover value' })}
        validators={['required', 'isPositive']}
        errorMessages={[
          t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' }),
          t(['shared:mustBePositive'], { defaultValue: 'must be a positive number' }),
          t(['shared:mustBePositive'], { defaultValue: 'must be a positive number' }),
        ]}
        value={state?.config?.sensor?.accumulatorConfig?.rolloverValue}
        onChange={onRolloverValueChange}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <FormControlLabel
          className={classes.checkboxField}
          control={
            <Checkbox
              checked={state?.config?.sensor?.accumulatorConfig?.resetsOnPowerOff ?? false}
              onChange={onPowerCheck}
              value="enabled"
              color="primary"
            />
          }
          label={`${t(['device:counterCanReset'], {
            defaultValue: 'Counter can reset (e.g. on power off or manually)',
          })}`}
        />
        {state?.config?.sensor?.accumulatorConfig?.resetsOnPowerOff ?? false ? (
          <InputValidator
            className={classes.textFields}
            containerClass={classes.containerClassTwoColumns}
            style={{ marginRight: 10 }}
            name="rolloverThreshold"
            type="number"
            label={t(['device:rolloverThreshold'], { defaultValue: 'Rollover threshold' })}
            validators={['required', 'isPositive', 'notZero']}
            errorMessages={[
              t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' }),
              t(['shared:mustBePositive'], { defaultValue: 'must be a positive number' }),
              t(['shared:mustBePositive'], { defaultValue: 'must be a positive number' }),
            ]}
            value={state?.config?.sensor?.accumulatorConfig?.rolloverThreshold}
            onChange={onRolloverThresholdChange}
          />
        ) : null}
      </div>
      {invalidFormMessage}
      <br />
      <WrapRequiredFieldsDescriptionComponent />
      <div className={classes.containerClass}>
        <FormControlLabel
          control={
            <Switch
              color="primary"
              checked={state.config?.sensor?.energyMeter ?? false}
              onChange={(e) => {
                if (e.target.checked && state.config?.sensor?.findStops) {
                  // Then we also disable findStops
                  handleChange({ sensor: { energyMeter: true, findStops: false } });
                } else {
                  handleChange({ sensor: { energyMeter: e.target.checked } });
                }
              }}
            />
          }
          label={t('device:energyMeter', { defaultValue: 'Energy Meter' })}
          className={classes.textFields}
        />
      </div>
      <WrapRequiredFieldsDescriptionComponent
        overrideTitle={t('device:energyMeterDescription', {
          defaultValue: 'Enable if the sensor is an energy meter.',
        })}
      />
    </div>
  );
};

const parseAnalogUnit = (range: Schema.AnalogInputRange | null | undefined): string => {
  const {
    NEGATIVE_FIVE_TO_FIVE_VOLTS,
    ZERO_TO_FIVE_VOLTS,
    ZERO_TO_TEN_VOLTS,
    NEGATIVE_TEN_TO_TEN_VOLTS,
    ZERO_TO_TWENTY_MILLIAMPS,
    ZERO_TO_TWENTYFOUR_MILLIAMPS,
    FOUR_TO_TWENTY_MILLIAMPS,
    NEGATIVE_TWENTYFIVE_TO_TWENTYFIVE_MILLIAMPS,
  } = Schema.AnalogInputRange;
  switch (range) {
    case ZERO_TO_FIVE_VOLTS:
    case ZERO_TO_TEN_VOLTS:
    case NEGATIVE_FIVE_TO_FIVE_VOLTS:
    case NEGATIVE_TEN_TO_TEN_VOLTS:
      return 'V';
    case ZERO_TO_TWENTY_MILLIAMPS:
    case ZERO_TO_TWENTYFOUR_MILLIAMPS:
    case FOUR_TO_TWENTY_MILLIAMPS:
    case NEGATIVE_TWENTYFIVE_TO_TWENTYFIVE_MILLIAMPS:
    default:
      return 'mA';
  }
};

enum AnalogInputOrMultiplier {
  UseAnalogInput,
  UseDataMultipler,
}

interface MeasurementContentProps {
  handleChange: (newConfig: Partial<UpdateConfigInput>, meta?: { name?: string; description?: string }) => void;
  state: SensorSettingsState;
  invalidFormMessage: React.ReactNode | null;
  classes: SensorSettingsClasses;
  t: TFunction;
  enumTypes: { analogInputRanges: Types.EnumTypes['analogInputRanges'] };
}

const MeasurementContent: FC<MeasurementContentProps> = (props) => {
  const { state, handleChange, classes, t, enumTypes, invalidFormMessage } = props;
  const { min: rangeMin, max: rangeMax } = parseAnalogRange(state?.config?.sensor?.analogInputRange!);
  const { sensorMin, sensorMax } = convertMultiplierOffset(
    state?.config?.sensor?.dataMultiplier ?? 1,
    state?.config?.sensor?.analogOffset || 0,
    state?.config?.sensor?.analogInputRange!,
  );
  const stringUnit = parseAnalogUnit(state?.config?.sensor?.analogInputRange);

  const onAnalogInputChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
    handleChange({ sensor: { analogInputRange: e.target.value as Schema.AnalogInputRange } });

  const onSensorMinChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    const newSensorMin = val.trim().length ? parseFloat(val) : 0;
    const { dataMultiplier, analogOffset } = convertSensorMinMax(
      newSensorMin,
      sensorMax,
      state?.config?.sensor?.analogInputRange!,
    );
    handleChange({ sensor: { dataMultiplier, analogOffset } });
  };

  const onSensorMaxChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    const newSensorMax = val.trim().length ? parseFloat(val) : 0;
    const { dataMultiplier, analogOffset } = convertSensorMinMax(
      sensorMin,
      newSensorMax,
      state?.config?.sensor?.analogInputRange!,
    );
    handleChange({ sensor: { dataMultiplier, analogOffset } });
  };

  const [selectedAnalogInputOrMultiplierState, setSelectedAnalogInputOrMultiplierState] =
    React.useState<AnalogInputOrMultiplier | null>(null);
  // only set value the first time
  if (selectedAnalogInputOrMultiplierState === null) {
    if (state?.config?.sensor?.analogInputRange) {
      setSelectedAnalogInputOrMultiplierState(AnalogInputOrMultiplier.UseAnalogInput);
    } else if (state?.config?.sensor?.dataMultiplier) {
      setSelectedAnalogInputOrMultiplierState(AnalogInputOrMultiplier.UseDataMultipler);
    }
  }

  const analogInputOrMultiplier = (
    <FormControl variant="filled" component="fieldset" style={{ padding: '20px' }}>
      <RadioGroup
        row
        name="row-radio-buttons-group"
        value={selectedAnalogInputOrMultiplierState?.toString()}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const val: AnalogInputOrMultiplier = +(event.target as HTMLInputElement).value;
          switch (val) {
            case AnalogInputOrMultiplier.UseAnalogInput:
              handleChange({ sensor: { dataMultiplier: null, analogOffset: 0 } });
              break;
            case AnalogInputOrMultiplier.UseDataMultipler:
              handleChange({ sensor: { analogInputRange: null } });
              break;
            default:
              break;
          }
          setSelectedAnalogInputOrMultiplierState(val);
        }}
      >
        <FormControlLabel
          value={AnalogInputOrMultiplier.UseAnalogInput.toString()}
          control={<Radio />}
          label={t(['device:useAnalogInputRange'], { defaultValue: 'Use analog input range' })}
        />
        <FormControlLabel
          value={AnalogInputOrMultiplier.UseDataMultipler.toString()}
          control={<Radio />}
          label={t(['device:useDataMultiplier'], { defaultValue: 'Use data multiplier' })}
        />
      </RadioGroup>
    </FormControl>
  );

  const analogInputRangeComponents = [
    <SelectValidator
      key={1}
      className={classes.selectFields}
      containerClass={classes.containerClass}
      name="type"
      label={`${t(['device:analogRange'], { defaultValue: 'Analog sensor range' })} *`}
      value={state?.config?.sensor?.analogInputRange || ''}
      validators={['required']}
      errorMessages={[t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' })]}
      helperText={
        !state?.config?.sensor?.analogInputRange ? (
          <span style={{ color: red[500] }}>
            {t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' })}
          </span>
        ) : (
          ''
        )
      }
      onChange={onAnalogInputChange}
    >
      <MenuItem key="disabled" value={''} disabled>
        {t(['device:selectAnalogRange'], { defaultValue: 'Select analog sensor range' })}
      </MenuItem>
      {(enumTypes.analogInputRanges ?? []).map((analogRange) => (
        <MenuItem style={{ paddingTop: '8px', paddingBottom: '8px' }} key={analogRange.name} value={analogRange.name}>
          <ListItemText primary={formatAnalogRange(t, analogRange.name)} secondary={analogRange.description} />
        </MenuItem>
      ))}
    </SelectValidator>,
    <br key={2} />,
    <br key={3} />,
    <div key={4} style={{ display: 'flex', justifyContent: 'space-between' }}>
      <InputValidator
        className={classes.textFields}
        containerClass={classes.containerClassTwoColumns}
        type="number"
        name="sensorMinimum"
        label={
          <div>
            {t(['device:sensorMinimumValue'], {
              defaultValue: 'Sensor minimum ({{min}})',
              min: `${rangeMin} ${stringUnit}`,
            })}{' '}
            *
          </div>
        }
        validators={['required']}
        errorMessages={[t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' })]}
        value={sensorMin}
        onChange={onSensorMinChange}
      />
      <InputValidator
        className={classes.textFields}
        containerClass={classes.containerClassTwoColumns}
        type="number"
        name="sensorMaximum"
        label={
          <div>
            {t(['device:sensorMaximumValue'], {
              defaultValue: 'Sensor maximum ({{max}})',
              max: `${rangeMax} ${stringUnit}`,
            })}{' '}
            *
          </div>
        }
        validators={['required']}
        errorMessages={[t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' })]}
        value={sensorMax}
        onChange={onSensorMaxChange}
      />
    </div>,
  ];

  const onDataMultiplierChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    handleChange({ sensor: { analogOffset: 0, dataMultiplier: val.trim().length ? parseFloat(val) : undefined } });
  };

  const dataMultiplierComponent = (
    <InputValidator
      className={classes.textFields}
      containerClass={classes.containerClass}
      name="dataMultiplier"
      type="number"
      label={t(['device:dataMultiplier'], { defaultValue: 'Data multiplier' })}
      validators={['required', 'isPositive']}
      errorMessages={[
        t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' }),
        t(['shared:mustBePositive'], { defaultValue: 'must be a positive number' }),
      ]}
      value={state?.config?.sensor?.dataMultiplier}
      helperText={
        !!state?.config?.sensor?.dataMultiplier && state?.config?.sensor?.dataMultiplier > 0 ? (
          <span>
            {t(['device:configurationDataMultiplierExplanation'], {
              defaultValue:
                'Future incoming data from the sensor will be multiplied by {{dataMultiplier}}. Old data will not be affected. If you change this value, remember to change target line speeds for your batches / products as well',
              dataMultiplier: state?.config?.sensor?.dataMultiplier ?? 1,
            })}
          </span>
        ) : undefined
      }
      onChange={onDataMultiplierChange}
    />
  );

  return (
    <div className={classes.tabPanel}>
      {analogInputOrMultiplier}
      <WiringSelection handleChange={handleChange} classes={classes} wiring={state.config?.sensor?.wiring || null} />
      {selectedAnalogInputOrMultiplierState === AnalogInputOrMultiplier.UseAnalogInput && analogInputRangeComponents}
      {selectedAnalogInputOrMultiplierState === AnalogInputOrMultiplier.UseDataMultipler && dataMultiplierComponent}
      {invalidFormMessage}
      <br />
      <WrapRequiredFieldsDescriptionComponent />
    </div>
  );
};
